(() => {
  class ElectronicPage extends HTMLElement {
    connectedCallback() {
      if (this.dataset.epInited === '1') return;
      this.dataset.epInited = '1';
      this._epCleanups = [];
      this.init();
    }

    disconnectedCallback() {
      this.destroy();
    }

    listen(target, type, handler, options) {
      if (!target || !type || !handler) return handler;
      target.addEventListener(type, handler, options);
      this._epCleanups.push(() => target.removeEventListener(type, handler, options));
      return handler;
    }

    destroy() {
      this.querySelectorAll('[data-ep-bms]').forEach((section) => this.pauseBmsAutoplay(section));
      this.querySelectorAll('[data-ep-usage]').forEach((section) => this.pauseUsageAutoplay(section));

      (this._epCleanups || []).forEach((fn) => {
        try {
          fn();
        } catch (err) {
          /* ignore teardown errors */
        }
      });
      this._epCleanups = [];

      this.querySelectorAll('[data-ep-story]').forEach((section) => {
        if (section._epStoryIo) {
          section._epStoryIo.disconnect();
          section._epStoryIo = null;
        }
        section._epStoryOnScroll = null;
      });

      // Clear init guards so reconnect (theme editor move/reorder) rebinds handlers
      const guardKeys = [
        'epBound',
        'epOutsideBound',
        'epResizeBound',
        'epComboBound',
        'epTabScrollBound',
        'epBuyBound',
        'epBuyOutsideBound',
        'epSafetyBound',
        'epSafetyScrollBound',
        'epSafetyNextBound',
        'epBmsBound',
        'epUsageBound',
        'epFaqBound',
        'epStoryBound'
      ];
      const clearGuards = (el) => {
        guardKeys.forEach((key) => {
          delete el.dataset[key];
        });
      };
      clearGuards(this);
      this.querySelectorAll([
        '[data-ep-bound]',
        '[data-ep-outside-bound]',
        '[data-ep-resize-bound]',
        '[data-ep-combo-bound]',
        '[data-ep-tab-scroll-bound]',
        '[data-ep-buy-bound]',
        '[data-ep-buy-outside-bound]',
        '[data-ep-safety-bound]',
        '[data-ep-safety-scroll-bound]',
        '[data-ep-safety-next-bound]',
        '[data-ep-bms-bound]',
        '[data-ep-usage-bound]',
        '[data-ep-faq-bound]',
        '[data-ep-story-bound]'
      ].join(',')).forEach(clearGuards);

      document.documentElement.classList.remove('ep-combo-modal-open', 'ep-safety-lightbox-open');
      delete this.dataset.epInited;
    }

    init() {
      this.bindSmoothAnchors();
      this.initStickyNav();
      this.initProductCombos();
      this.initSafetyTests();
      this.initBmsShowcases();
      this.initUsageScenes();
      this.initFaqs();
      this.initStoryScrolls();
    }

    bindSmoothAnchors() {
      const links = this.querySelectorAll('a[data-ep-scroll]');
      links.forEach((link) => {
        link.addEventListener('click', (event) => {
          const target = this.resolveScrollTarget(link.getAttribute('href'));
          if (!target) return;
          event.preventDefault();
          this.closeStickyNav();
          this.scrollToTarget(target);
        });
      });
    }

    normalizeAnchorKey(raw) {
      if (!raw) return '';
      let value = String(raw).trim();
      if (!value || value === '#') return '';
      try {
        if (value.startsWith('http://') || value.startsWith('https://') || value.startsWith('/')) {
          value = new URL(value, window.location.href).hash || '';
        }
      } catch (err) {
        /* keep value */
      }
      const hashIndex = value.lastIndexOf('#');
      if (hashIndex >= 0) value = value.slice(hashIndex + 1);
      return value.replace(/^#/, '').trim();
    }

    resolveScrollTarget(raw) {
      const key = this.normalizeAnchorKey(raw);
      if (!key) return null;

      // Prefer targets inside this section instance (multi electronic-page safe)
      let scoped = null;
      try {
        scoped = this.querySelector(`#${CSS.escape(key)}`);
      } catch (err) {
        scoped = null;
      }
      if (scoped) return scoped;

      scoped = this.querySelector(`[data-anchor="${key}"]`);
      if (scoped) return scoped;

      const aliases = {
        'ep-products': ['.ep-combo'],
        'ep-combo': ['.ep-combo'],
        'ep-safety': ['.ep-safety'],
        'ep-compare': ['.ep-cmp'],
        'ep-bms': ['.ep-bms'],
        'ep-usage': ['.ep-usage'],
        'ep-advantages': ['.ep-adv'],
        'ep-faq': ['.ep-faq'],
        'ep-story': ['.ep-story']
      };
      const selectors = aliases[key] || [];
      for (let i = 0; i < selectors.length; i += 1) {
        const el = this.querySelector(selectors[i]);
        if (el) return el;
      }

      // Fallback for absolute ids / anchors outside this section
      const byId = document.getElementById(key);
      if (byId) return byId;

      const byAttr = document.querySelector(`[data-anchor="${key}"]`);
      if (byAttr) return byAttr;

      for (let i = 0; i < selectors.length; i += 1) {
        const el = document.querySelector(selectors[i]);
        if (el) return el;
      }
      return null;
    }

    getAnchorScrollOffset() {
      let offset = 0;
      const stickyNav = this.getStickyNav();
      if (stickyNav) offset += stickyNav.getBoundingClientRect().height;
      const header = document.querySelector('#shopify-section-header, .t4s-section-header');
      if (header) {
        const style = window.getComputedStyle(header);
        if (style.position === 'fixed' || style.position === 'sticky') {
          const rect = header.getBoundingClientRect();
          if (rect.height > 0 && rect.bottom > 0 && rect.top < 2) {
            offset += rect.height;
          }
        }
      }
      return offset;
    }

    scrollToTarget(target) {
      const offset = this.getAnchorScrollOffset();
      const top = window.scrollY + target.getBoundingClientRect().top - offset;
      window.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    }

    getStickyNav() {
      return this.querySelector('.ep-sticky-nav');
    }

    closeStickyNav() {
      const stickyNav = this.getStickyNav();
      if (!stickyNav) return;
      stickyNav.classList.remove('is-open');
      const toggle = stickyNav.querySelector('.ep-sticky-nav__toggle');
      if (toggle) toggle.setAttribute('aria-expanded', 'false');
    }

    syncStickyNavOffset() {
      const stickyNav = this.getStickyNav();
      if (!stickyNav) {
        this.style.setProperty('--ep-sticky-nav-h', '0px');
        return;
      }
      // Measure the bar only (not the open MB dropdown)
      const bar = stickyNav.querySelector('.ep-sticky-nav__inner') || stickyNav;
      const height = Math.round(bar.getBoundingClientRect().height) || 0;
      this.style.setProperty('--ep-sticky-nav-h', `${height}px`);
    }

    initStickyNav() {
      const stickyNav = this.getStickyNav();
      if (!stickyNav) {
        this.syncStickyNavOffset();
        return;
      }

      const toggle = stickyNav.querySelector('.ep-sticky-nav__toggle');
      if (toggle && !toggle.dataset.epBound) {
        toggle.dataset.epBound = '1';
        toggle.addEventListener('click', () => {
          if (window.innerWidth > 1023.98) return;
          const isOpen = stickyNav.classList.toggle('is-open');
          toggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
        });
      }

      if (!stickyNav.dataset.epOutsideBound) {
        stickyNav.dataset.epOutsideBound = '1';
        this.listen(document, 'click', (event) => {
          if (window.innerWidth > 1023.98) return;
          if (!stickyNav.classList.contains('is-open')) return;
          if (stickyNav.contains(event.target)) return;
          this.closeStickyNav();
        });
      }

      if (!stickyNav.dataset.epResizeBound) {
        stickyNav.dataset.epResizeBound = '1';
        this.listen(window, 'resize', () => {
          if (window.innerWidth > 1023.98) this.closeStickyNav();
          this.syncStickyNavOffset();
        }, { passive: true });
      }

      this.syncStickyNavOffset();
    }

    initProductCombos() {
      const combos = this.querySelectorAll('.ep-combo');
      combos.forEach((combo) => this.initProductCombo(combo));
    }

    initProductCombo(combo) {
      if (combo.dataset.epComboBound === '1') return;
      combo.dataset.epComboBound = '1';

      this.bindComboTabs(combo);
      this.bindComboCopy(combo);
      this.bindComboBuy(combo);
      this.bindComboMore(combo);
      this.applyComboLimits(combo);
      const comboTabs = combo.querySelector('[data-ep-tablist]');
      this.syncTabGlider(comboTabs);
      this.syncComboTitleHeights(combo);
      requestAnimationFrame(() => {
        this.syncTabGlider(comboTabs);
        this.syncComboTitleHeights(combo);
      });

      this.listen(window, 'resize', () => {
        this.closeAllComboDropdowns(combo);
        this.closeComboModal(combo);
        this.applyComboLimits(combo);
        this.syncTabGlider(comboTabs);
        this.syncComboTitleHeights(combo);
      }, { passive: true });
    }

    syncTabGlider(tabsEl) {
      if (!tabsEl) return;
      const glider = tabsEl.querySelector('[data-ep-tab-glider]');
      const active = tabsEl.querySelector('[role="tab"].is-active');
      if (!glider || !active) return;

      const parentRect = tabsEl.getBoundingClientRect();
      const rect = active.getBoundingClientRect();
      const width = rect.width;
      const height = rect.height;
      if (width < 2 || height < 2) return;

      const x = rect.left - parentRect.left + tabsEl.scrollLeft;
      const y = rect.top - parentRect.top + tabsEl.scrollTop;

      glider.style.width = `${Math.round(width)}px`;
      glider.style.height = `${Math.round(height)}px`;
      glider.style.transform = `translate3d(${Math.round(x)}px, ${Math.round(y)}px, 0)`;
      glider.classList.add('is-ready');
    }

    getActiveComboPanel(combo) {
      return combo.querySelector('.ep-combo__panel.is-active') || combo.querySelector('.ep-combo__panel');
    }

    getComboLimit(combo) {
      const isMb = window.innerWidth <= 1023.98;
      const raw = isMb ? combo.dataset.mbLimit : combo.dataset.pcLimit;
      const limit = Number(raw);
      return Number.isFinite(limit) && limit > 0 ? limit : (isMb ? 4 : 3);
    }

    applyComboLimits(combo) {
      if (combo.classList.contains('is-expanded')) {
        combo.querySelectorAll('.ep-combo__card').forEach((card) => {
          card.hidden = false;
        });
        this.updateComboMoreVisibility(combo);
        return;
      }

      const limit = this.getComboLimit(combo);
      combo.querySelectorAll('.ep-combo__panel').forEach((panel) => {
        const cards = panel.querySelectorAll('.ep-combo__card');
        cards.forEach((card, index) => {
          card.hidden = index >= limit;
        });
      });
      this.updateComboMoreVisibility(combo);
    }

    syncComboTitleHeights(combo) {
      const panel = this.getActiveComboPanel(combo);
      if (!panel) return;
      const cards = Array.from(panel.querySelectorAll('.ep-combo__card'))
        .filter((card) => !card.hidden);
      const titles = cards
        .map((card) => card.querySelector('.ep-combo__card-title'))
        .filter(Boolean);

      titles.forEach((title) => {
        title.style.minHeight = '';
      });
      if (titles.length < 2) return;

      requestAnimationFrame(() => {
        const rows = new Map();
        cards.forEach((card) => {
          const title = card.querySelector('.ep-combo__card-title');
          if (!title) return;
          const top = Math.round(card.offsetTop);
          if (!rows.has(top)) rows.set(top, []);
          rows.get(top).push(title);
        });
        rows.forEach((rowTitles) => {
          const maxH = rowTitles.reduce((max, title) => Math.max(max, title.scrollHeight), 0);
          rowTitles.forEach((title) => {
            title.style.minHeight = `${maxH}px`;
          });
        });
      });
    }

    updateComboMoreVisibility(combo) {
      const moreBtn = combo.querySelector('[data-ep-more]');
      if (!moreBtn) return;
      const panel = this.getActiveComboPanel(combo);
      if (!panel) {
        moreBtn.hidden = true;
        return;
      }
      const cards = panel.querySelectorAll('.ep-combo__card');
      const limit = this.getComboLimit(combo);
      const needsMore = cards.length > limit;
      moreBtn.hidden = !needsMore && !combo.classList.contains('is-expanded');
      if (!needsMore && combo.classList.contains('is-expanded')) {
        combo.classList.remove('is-expanded');
      }
      this.syncComboMoreLabel(combo);
    }

    syncComboMoreLabel(combo) {
      const moreBtn = combo.querySelector('[data-ep-more]');
      if (!moreBtn) return;
      const textEl = moreBtn.querySelector('[data-ep-more-text]');
      const icon = moreBtn.querySelector('.ep-combo__more-icon');
      const moreText = combo.dataset.moreText || 'もっと見る';
      const lessText = combo.dataset.lessText || '閉じる';
      const expanded = combo.classList.contains('is-expanded');
      if (textEl) textEl.textContent = expanded ? lessText : moreText;
      if (icon) icon.textContent = expanded ? '⌃' : '⌄';
      moreBtn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
    }

    bindComboTabs(combo) {
      const tabsEl = combo.querySelector('[data-ep-tablist]');
      const tabs = combo.querySelectorAll('[data-ep-tab]');
      if (!tabs.length) return;

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const id = tab.getAttribute('data-ep-tab');
          tabs.forEach((item) => {
            const active = item === tab;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          combo.querySelectorAll('.ep-combo__panel').forEach((panel) => {
            const active = panel.getAttribute('data-ep-panel') === id;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
          combo.classList.remove('is-expanded');
          this.closeAllComboDropdowns(combo);
          this.applyComboLimits(combo);
          this.syncComboTitleHeights(combo);
          if (typeof tab.scrollIntoView === 'function') {
            tab.scrollIntoView({ behavior: 'smooth', inline: 'nearest', block: 'nearest' });
          }
          requestAnimationFrame(() => this.syncTabGlider(tabsEl));
        });
      });

      if (tabsEl && !tabsEl.dataset.epTabScrollBound) {
        tabsEl.dataset.epTabScrollBound = '1';
        tabsEl.addEventListener('scroll', () => this.syncTabGlider(tabsEl), { passive: true });
      }
    }

    bindComboCopy(combo) {
      combo.querySelectorAll('[data-ep-copy]').forEach((btn) => {
        btn.addEventListener('click', async () => {
          const code = btn.getAttribute('data-ep-copy') || '';
          if (!code) return;
          const original = btn.textContent;
          try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
              await navigator.clipboard.writeText(code);
            } else {
              const input = document.createElement('input');
              input.value = code;
              document.body.appendChild(input);
              input.select();
              document.execCommand('copy');
              input.remove();
            }
            btn.textContent = combo.dataset.copiedText || 'コピーしました';
            btn.classList.add('is-copied');
            setTimeout(() => {
              btn.textContent = original;
              btn.classList.remove('is-copied');
            }, 1500);
          } catch (error) {
            btn.textContent = original;
          }
        });
      });
    }

    closeAllComboDropdowns(combo, except) {
      combo.querySelectorAll('[data-ep-dropdown]').forEach((dropdown) => {
        if (except && dropdown === except) return;
        const wrap = dropdown.closest('.ep-combo__buy-wrap');
        if (wrap) wrap.classList.remove('is-open');
      });
    }

    closeComboModal(combo) {
      const modal = combo.querySelector('[data-ep-modal]');
      if (!modal) return;
      modal.hidden = true;
      document.documentElement.classList.remove('ep-combo-modal-open');
      const platforms = modal.querySelector('[data-ep-modal-platforms]');
      if (platforms) platforms.innerHTML = '';
    }

    openComboModal(combo, dropdown) {
      const modal = combo.querySelector('[data-ep-modal]');
      const platforms = modal && modal.querySelector('[data-ep-modal-platforms]');
      if (!modal || !platforms || !dropdown) return;
      platforms.innerHTML = '';
      dropdown.querySelectorAll('.ep-combo__platform').forEach((link) => {
        const clone = link.cloneNode(true);
        clone.addEventListener('click', () => this.closeComboModal(combo));
        platforms.appendChild(clone);
      });
      modal.hidden = false;
      document.documentElement.classList.add('ep-combo-modal-open');
    }

    bindComboBuy(combo) {
      combo.querySelectorAll('.ep-combo__buy-wrap').forEach((wrap) => {
        if (wrap.dataset.epBuyBound === '1') return;
        wrap.dataset.epBuyBound = '1';

        const btn = wrap.querySelector('[data-ep-buy]');
        const dropdown = wrap.querySelector('[data-ep-dropdown]');
        if (!btn || !dropdown) return;

        btn.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();
          const links = dropdown.querySelectorAll('.ep-combo__platform');
          if (!links.length) return;

          // MB: 始终打开平台选择弹窗，再跳转对应平台
          if (window.innerWidth <= 1023.98) {
            this.closeAllComboDropdowns(combo);
            this.openComboModal(combo, dropdown);
            return;
          }

          // PC: 仅一个平台时直接跳转；多个则 hover/点击展开下拉
          if (links.length === 1) {
            const href = links[0].getAttribute('href');
            if (href) window.open(href, '_blank', 'noopener');
            return;
          }

          const willOpen = !wrap.classList.contains('is-open');
          this.closeAllComboDropdowns(combo, dropdown);
          wrap.classList.toggle('is-open', willOpen);
        });
      });

      if (!combo.dataset.epBuyOutsideBound) {
        combo.dataset.epBuyOutsideBound = '1';
        this.listen(document, 'click', (event) => {
          if (combo.contains(event.target) && event.target.closest('.ep-combo__buy-wrap')) return;
          this.closeAllComboDropdowns(combo);
        });
      }

      combo.querySelectorAll('[data-ep-modal-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeComboModal(combo));
      });
    }

    bindComboMore(combo) {
      const moreBtn = combo.querySelector('[data-ep-more]');
      if (!moreBtn) return;
      moreBtn.addEventListener('click', () => {
        combo.classList.toggle('is-expanded');
        this.applyComboLimits(combo);
        this.syncComboTitleHeights(combo);
      });
    }

    initSafetyTests() {
      const sections = this.querySelectorAll('.ep-safety');
      sections.forEach((section) => this.initSafetySection(section));
    }

    initSafetySection(section) {
      if (section.dataset.epSafetyBound === '1') return;
      section.dataset.epSafetyBound = '1';

      this.bindSafetyTabs(section);
      this.bindSafetyVideo(section);
      const safetyTabs = section.querySelector('[data-ep-tablist]');
      this.syncTabGlider(safetyTabs);
      this.syncSafetyFeatsBars(section);
      requestAnimationFrame(() => {
        this.syncTabGlider(safetyTabs);
        this.syncSafetyFeatsBars(section);
      });

      this.listen(window, 'resize', () => {
        this.syncTabGlider(safetyTabs);
        this.syncSafetyTabsFade(section);
        this.syncSafetyFeatsBars(section);
      }, { passive: true });
    }

    syncSafetyFeatsBars(section) {
      const bars = section.querySelectorAll('[data-ep-safety-feats]');
      bars.forEach((bar) => this.syncSafetyFeatsBar(bar));
    }

    syncSafetyFeatsBar(bar) {
      if (!bar) return;
      if (window.innerWidth > 1023.98) {
        bar.classList.remove('is-stacked');
        return;
      }
      bar.classList.remove('is-stacked');
      const texts = bar.querySelectorAll('.ep-safety__feat-text');
      let needsStack = false;
      texts.forEach((el) => {
        if (el.scrollWidth > el.clientWidth + 1) needsStack = true;
      });
      bar.classList.toggle('is-stacked', needsStack);
    }

    bindSafetyTabs(section) {
      const tabsEl = section.querySelector('[data-ep-tablist]');
      const tabs = section.querySelectorAll('[data-ep-safety-tab]');
      if (!tabs.length) return;

      tabs.forEach((tab) => {
        tab.addEventListener('click', () => {
          const id = tab.getAttribute('data-ep-safety-tab');
          tabs.forEach((item) => {
            const active = item === tab;
            item.classList.toggle('is-active', active);
            item.setAttribute('aria-selected', active ? 'true' : 'false');
          });
          section.querySelectorAll('[data-ep-safety-panel]').forEach((panel) => {
            const active = panel.getAttribute('data-ep-safety-panel') === id;
            panel.classList.toggle('is-active', active);
            panel.hidden = !active;
          });
          this.scrollSafetyTabIntoView(tab);
          this.syncTabGlider(tabsEl);
          this.syncSafetyTabsFade(section);
          this.syncSafetyFeatsBars(section);
          // smooth scroll 结束后再校正滑块，避免最后一项对不齐
          window.setTimeout(() => {
            this.syncTabGlider(tabsEl);
            this.syncSafetyTabsFade(section);
          }, 320);
        });
      });

      if (tabsEl && !tabsEl.dataset.epSafetyScrollBound) {
        tabsEl.dataset.epSafetyScrollBound = '1';
        tabsEl.addEventListener('scroll', () => {
          this.syncTabGlider(tabsEl);
          this.syncSafetyTabsFade(section);
        }, { passive: true });
      }

      const nextBtn = section.querySelector('[data-ep-safety-tabs-next]');
      if (nextBtn && !nextBtn.dataset.epSafetyNextBound) {
        nextBtn.dataset.epSafetyNextBound = '1';
        nextBtn.addEventListener('click', (event) => {
          event.preventDefault();
          this.scrollSafetyTabsNext(section);
        });
      }

      this.syncSafetyTabsFade(section);
    }

    syncSafetyTabsFade(section) {
      const wrap = section.querySelector('.ep-safety__tabs-wrap');
      const tabsEl = section.querySelector('[data-ep-tablist]');
      if (!wrap || !tabsEl) return;
      if (window.innerWidth > 1023.98) {
        wrap.classList.remove('is-scrollable', 'is-at-end');
        return;
      }
      const maxScroll = Math.max(0, tabsEl.scrollWidth - tabsEl.clientWidth);
      const canScroll = maxScroll > 2;
      const atEnd = tabsEl.scrollLeft >= maxScroll - 2;
      wrap.classList.toggle('is-scrollable', canScroll);
      wrap.classList.toggle('is-at-end', !canScroll || atEnd);
    }

    scrollSafetyTabsNext(section) {
      if (window.innerWidth > 1023.98) return;
      const tabsEl = section.querySelector('[data-ep-tablist]');
      if (!tabsEl) return;
      const step = Math.max(tabsEl.clientWidth * 0.55, 96);
      tabsEl.scrollBy({ left: step, behavior: 'smooth' });
      window.setTimeout(() => {
        this.syncTabGlider(tabsEl);
        this.syncSafetyTabsFade(section);
      }, 320);
    }

    scrollSafetyTabIntoView(tab) {
      if (window.innerWidth > 1023.98) return;
      const scroller = tab.closest('.ep-safety__tabs');
      if (!scroller) return;

      const scrollerWidth = scroller.clientWidth;
      const tabLeft = tab.offsetLeft;
      const tabWidth = tab.offsetWidth;
      const maxScroll = Math.max(0, scroller.scrollWidth - scrollerWidth);
      // 尽量居中；最后几项会自然贴右，配合 padding-right 避开箭头遮罩
      let target = tabLeft - (scrollerWidth - tabWidth) / 2;
      target = Math.max(0, Math.min(target, maxScroll));
      scroller.scrollTo({ left: target, behavior: 'smooth' });
    }

    bindSafetyVideo(section) {
      const lightbox = section.querySelector('[data-ep-safety-lightbox]');
      const player = section.querySelector('[data-ep-safety-player]');
      if (!lightbox || !player) return;

      section.querySelectorAll('[data-ep-safety-video]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const url = btn.getAttribute('data-video-url');
          if (!url) return;
          this.openSafetyLightbox(lightbox, player, url);
        });
      });

      section.querySelectorAll('[data-ep-safety-lightbox-close]').forEach((el) => {
        el.addEventListener('click', () => this.closeSafetyLightbox(lightbox, player));
      });

      this.listen(document, 'keydown', (event) => {
        if (event.key !== 'Escape') return;
        if (lightbox.hidden) return;
        this.closeSafetyLightbox(lightbox, player);
      });
    }

    openSafetyLightbox(lightbox, player, url) {
      player.innerHTML = '';
      const embed = this.buildSafetyVideoEmbed(url);
      if (!embed) return;
      player.appendChild(embed);
      lightbox.hidden = false;
      document.documentElement.classList.add('ep-safety-lightbox-open');
    }

    closeSafetyLightbox(lightbox, player) {
      lightbox.hidden = true;
      player.innerHTML = '';
      document.documentElement.classList.remove('ep-safety-lightbox-open');
    }

    buildSafetyVideoEmbed(url) {
      const youtubeId = this.extractYoutubeId(url);
      if (youtubeId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://www.youtube.com/embed/${youtubeId}?autoplay=1&rel=0`;
        iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = 'YouTube video';
        return iframe;
      }

      const vimeoId = this.extractVimeoId(url);
      if (vimeoId) {
        const iframe = document.createElement('iframe');
        iframe.src = `https://player.vimeo.com/video/${vimeoId}?autoplay=1`;
        iframe.allow = 'autoplay; fullscreen; picture-in-picture';
        iframe.allowFullscreen = true;
        iframe.title = 'Vimeo video';
        return iframe;
      }

      const video = document.createElement('video');
      video.src = url;
      video.controls = true;
      video.autoplay = true;
      video.playsInline = true;
      return video;
    }

    extractYoutubeId(url) {
      try {
        const parsed = new URL(url);
        if (parsed.hostname.includes('youtu.be')) {
          return parsed.pathname.replace('/', '') || null;
        }
        if (parsed.hostname.includes('youtube.com')) {
          if (parsed.pathname.startsWith('/embed/')) {
            return parsed.pathname.split('/')[2] || null;
          }
          if (parsed.pathname.startsWith('/shorts/')) {
            return parsed.pathname.split('/')[2] || null;
          }
          return parsed.searchParams.get('v');
        }
      } catch (error) {
        return null;
      }
      return null;
    }

    extractVimeoId(url) {
      try {
        const parsed = new URL(url);
        if (!parsed.hostname.includes('vimeo.com')) return null;
        const parts = parsed.pathname.split('/').filter(Boolean);
        return parts[parts.length - 1] || null;
      } catch (error) {
        return null;
      }
    }

    initBmsShowcases() {
      const sections = this.querySelectorAll('[data-ep-bms]');
      sections.forEach((section) => this.initBmsShowcase(section));
    }

    initBmsShowcase(section) {
      if (section.dataset.epBmsBound === '1') return;
      section.dataset.epBmsBound = '1';

      const ids = Array.from(section.querySelectorAll('[data-ep-bms-item]'))
        .map((el) => el.getAttribute('data-ep-bms-item'))
        .filter(Boolean);
      if (!ids.length) {
        // fallback for older markup
        const pillIds = Array.from(section.querySelectorAll('[data-ep-bms-pill]'))
          .map((el) => el.getAttribute('data-ep-bms-pill'))
          .filter(Boolean);
        if (!pillIds.length) return;
        section._epBmsIds = pillIds;
      } else {
        section._epBmsIds = ids;
      }

      const activeBubble = section.querySelector('.ep-bms__bubble.is-open');
      const activeId = activeBubble
        ? activeBubble.getAttribute('data-ep-bms-item')
        : section._epBmsIds[0];
      section._epBmsIndex = Math.max(0, section._epBmsIds.indexOf(activeId || section._epBmsIds[0]));
      section._epBmsSlideId = section._epBmsIds[section._epBmsIndex];
      this.syncBmsMbPanel(section, section._epBmsSlideId);

      const goTo = (index, { user } = {}) => {
        const next = ((index % section._epBmsIds.length) + section._epBmsIds.length) % section._epBmsIds.length;
        section._epBmsIndex = next;
        this.setBmsSlide(section, section._epBmsIds[next]);
        this.updateBmsUiTrack(section);
        if (user) this.restartBmsAutoplay(section);
      };

      this.bindBmsSwipe(section, goTo);

      section.querySelectorAll('[data-ep-bms-pill]').forEach((pill) => {
        pill.addEventListener('click', () => {
          const id = pill.getAttribute('data-ep-bms-pill');
          goTo(section._epBmsIds.indexOf(id), { user: true });
        });
      });

      section.querySelectorAll('[data-ep-bms-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          const id = dot.getAttribute('data-ep-bms-dot');
          goTo(section._epBmsIds.indexOf(id), { user: true });
        });
      });

      section.querySelectorAll('[data-ep-bms-prev]').forEach((btn) => {
        btn.addEventListener('click', () => goTo(section._epBmsIndex - 1, { user: true }));
      });

      section.querySelectorAll('[data-ep-bms-next]').forEach((btn) => {
        btn.addEventListener('click', () => goTo(section._epBmsIndex + 1, { user: true }));
      });

      // MB：贴边半圆 = 上一项/下一项；点击收缩侧切换
      section.querySelectorAll('[data-ep-bms-mb-item]').forEach((item) => {
        item.addEventListener('click', () => {
          if (item.classList.contains('is-prev')) {
            goTo(section._epBmsIndex - 1, { user: true });
            return;
          }
          if (item.classList.contains('is-next')) {
            goTo(section._epBmsIndex + 1, { user: true });
          }
        });
      });

      const syncUi = () => {
        this.updateBmsUiTrack(section);
        this.updateBmsMbPanelHeight(section);
      };
      this.syncBmsLastTip(section);
      syncUi();
      requestAnimationFrame(syncUi);
      this.listen(window, 'resize', syncUi, { passive: true });
      section.querySelectorAll('.ep-bms__product img, .ep-bms__ui-item img').forEach((img) => {
        if (img.complete) return;
        img.addEventListener('load', syncUi, { once: true });
      });

      section.addEventListener('mouseenter', () => this.pauseBmsAutoplay(section));
      section.addEventListener('mouseleave', () => this.startBmsAutoplay(section));
      section.addEventListener('touchstart', () => this.pauseBmsAutoplay(section), { passive: true });
      section.addEventListener('touchend', () => this.startBmsAutoplay(section), { passive: true });

      this.startBmsAutoplay(section);
    }

    resetBmsBubbleStyles(el) {
      if (el._epBmsAnimTimer) {
        window.clearTimeout(el._epBmsAnimTimer);
        el._epBmsAnimTimer = null;
      }
      el._epBmsAnimFinish = null;
      if (el._epBmsAnim) {
        try {
          el._epBmsAnim.cancel();
        } catch (e) { /* noop */ }
        el._epBmsAnim = null;
      }
      // Cancel any leftover WAAPI fills that can freeze width/height
      if (typeof el.getAnimations === 'function') {
        el.getAnimations().forEach((anim) => {
          try {
            anim.cancel();
          } catch (e) { /* noop */ }
        });
      }
      el.classList.remove('is-animating', 'is-collapsing');
      el.style.width = '';
      el.style.height = '';
      el.style.overflow = '';
      el.style.transition = '';
    }

    setBmsSlide(section, id) {
      if (section._epBmsSlideId === id) {
        this.syncBmsMedia(section, id);
        return;
      }

      section._epBmsSlideId = id;
      this.syncBmsMedia(section, id);
      this.syncBmsLastTip(section);

      const bubbles = Array.from(section.querySelectorAll('[data-ep-bms-item]'));
      if (!bubbles.length) return;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
        || window.innerWidth <= 1023.98;

      bubbles.forEach((el) => {
        const shouldOpen = el.getAttribute('data-ep-bms-item') === id;
        const isOpen = el.classList.contains('is-open');
        const toggle = el.querySelector('[data-ep-bms-pill]');
        if (toggle) toggle.setAttribute('aria-expanded', shouldOpen ? 'true' : 'false');

        if (reduced) {
          this.resetBmsBubbleStyles(el);
          el.classList.toggle('is-open', shouldOpen);
          return;
        }

        if (shouldOpen) {
          if (!isOpen || el.classList.contains('is-animating')) {
            this.animateBmsBubble(el, true);
          }
          return;
        }

        // Non-active: animate close if open, otherwise hard-reset any stuck WAAPI height
        if (isOpen || el.classList.contains('is-animating')) {
          this.animateBmsBubble(el, false);
        } else {
          this.resetBmsBubbleStyles(el);
          el.classList.remove('is-open');
        }
      });
    }

    animateBmsBubble(el, shouldOpen) {
      const parent = el.parentElement;
      if (!parent) {
        this.resetBmsBubbleStyles(el);
        el.classList.toggle('is-open', shouldOpen);
        return;
      }

      // Snapshot current painted size, then hard-reset any leftover WAAPI fills
      const fromW = el.offsetWidth;
      const fromH = el.offsetHeight;
      this.resetBmsBubbleStyles(el);

      el.classList.add('is-animating');
      el.classList.toggle('is-collapsing', !shouldOpen);
      el.classList.toggle('is-open', true); // keep open chrome while collapsing; panel leaves flow via CSS

      el.style.transition = 'none';
      el.style.overflow = 'hidden';
      el.style.width = shouldOpen ? '100%' : 'max-content';
      el.style.height = 'auto';
      const toW = Math.max(1, el.offsetWidth);
      const toH = Math.max(1, el.offsetHeight);

      el.style.width = `${fromW}px`;
      el.style.height = `${fromH}px`;

      const duration = shouldOpen ? 460 : 320;
      const easing = shouldOpen
        ? 'cubic-bezier(0.22, 1, 0.36, 1)'
        : 'cubic-bezier(0.4, 0, 0.2, 1)';

      const applyEndState = () => {
        el.classList.toggle('is-open', shouldOpen);
        el.classList.remove('is-animating', 'is-collapsing');
        el.style.width = '';
        el.style.height = '';
        el.style.overflow = '';
        el.style.transition = '';
        el._epBmsAnim = null;
      };

      if (typeof el.animate !== 'function' || (fromW === toW && fromH === toH)) {
        applyEndState();
        return;
      }

      const anim = el.animate(
        [
          { width: `${fromW}px`, height: `${fromH}px` },
          { width: `${toW}px`, height: `${toH}px` },
        ],
        { duration, easing, fill: 'forwards' }
      );
      el._epBmsAnim = anim;

      let settled = false;
      const finish = () => {
        if (settled || el._epBmsAnimFinish !== finish) return;
        settled = true;
        el._epBmsAnimFinish = null;
        window.clearTimeout(el._epBmsAnimTimer);
        el._epBmsAnimTimer = null;

        // Critical: release fill:forwards so class-based width/height can take over
        try {
          anim.commitStyles();
        } catch (e) { /* noop */ }
        try {
          anim.cancel();
        } catch (e) { /* noop */ }

        applyEndState();
      };
      el._epBmsAnimFinish = finish;

      anim.addEventListener('finish', finish);
      el._epBmsAnimTimer = window.setTimeout(finish, duration + 80);
    }

    resetBmsProgress(section) {
      const rings = section.querySelectorAll('[data-ep-bms-progress]');
      const circumference = 2 * Math.PI * 21;
      rings.forEach((ring) => {
        ring.style.transition = 'none';
        ring.style.strokeDasharray = `${circumference}`;
        ring.style.strokeDashoffset = `${circumference}`;
      });

      section.querySelectorAll('[data-ep-bms-dot-fill]').forEach((fill) => {
        fill.style.transition = 'none';
        fill.style.width = '0%';
      });
    }

    startBmsProgress(section) {
      const interval = Number(section.dataset.interval) || 5000;
      const rings = section.querySelectorAll('[data-ep-bms-progress]');
      const fills = section.querySelectorAll('[data-ep-bms-dot-fill]');
      if (!rings.length && !fills.length) return;

      const circumference = 2 * Math.PI * 21;
      rings.forEach((ring) => {
        ring.style.transition = 'none';
        ring.style.strokeDasharray = `${circumference}`;
        ring.style.strokeDashoffset = `${circumference}`;
      });

      fills.forEach((fill) => {
        fill.style.transition = 'none';
        fill.style.width = '0%';
      });

      // force reflow so the next transition always restarts from 0%
      void section.offsetWidth;

      // PC：下一页箭头转圈
      rings.forEach((ring) => {
        ring.style.transition = `stroke-dashoffset ${interval}ms linear`;
        ring.style.strokeDashoffset = '0';
      });

      // MB：仅当前 pagination 填充进度
      fills.forEach((fill) => {
        const active = fill.closest('.ep-bms__dot')?.classList.contains('is-active');
        if (!active) return;
        fill.style.transition = `width ${interval}ms linear`;
        fill.style.width = '100%';
      });
    }

    pauseBmsProgress(section) {
      section.querySelectorAll('[data-ep-bms-progress]').forEach((ring) => {
        const computed = window.getComputedStyle(ring).strokeDashoffset;
        ring.style.transition = 'none';
        ring.style.strokeDashoffset = computed;
      });

      section.querySelectorAll('[data-ep-bms-dot-fill]').forEach((fill) => {
        const computed = window.getComputedStyle(fill).width;
        fill.style.transition = 'none';
        fill.style.width = computed;
      });
    }

    syncBmsMedia(section, id) {
      section.querySelectorAll('[data-ep-bms-image]').forEach((image) => {
        const active = image.getAttribute('data-ep-bms-image') === id;
        image.classList.toggle('is-active', active);
        image.hidden = !active;
      });

      section.querySelectorAll('[data-ep-bms-ui]').forEach((ui) => {
        ui.classList.toggle('is-active', ui.getAttribute('data-ep-bms-ui') === id);
      });

      section.querySelectorAll('[data-ep-bms-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot.getAttribute('data-ep-bms-dot') === id);
      });

      section.querySelectorAll('[data-ep-bms-mb-slide]').forEach((slide) => {
        const active = slide.getAttribute('data-ep-bms-mb-slide') === id;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
      });

      this.syncBmsMbPanel(section, id);
    }

    setBmsMbItemRole(item, role) {
      item.classList.toggle('is-active', role === 'active');
      item.classList.toggle('is-prev', role === 'prev');
      item.classList.toggle('is-next', role === 'next');
      item.classList.toggle('is-exit-left', role === 'exit-left');
      item.classList.toggle('is-exit-right', role === 'exit-right');
      const interactive = role === 'prev' || role === 'next';
      item.tabIndex = interactive ? 0 : -1;
      item.setAttribute('aria-current', role === 'active' ? 'true' : 'false');
      item.setAttribute('aria-hidden', role === 'active' || interactive ? 'false' : 'true');
    }

    syncBmsMbPanel(section, id) {
      const ids = section._epBmsIds || [];
      const items = Array.from(section.querySelectorAll('[data-ep-bms-mb-item]'));
      if (!items.length || !ids.length) return;

      const activeIndex = Math.max(0, ids.indexOf(id));
      const prevId = ids[(activeIndex - 1 + ids.length) % ids.length];
      const nextId = ids[(activeIndex + 1) % ids.length];
      const canNav = ids.length > 1;
      const isInit = section._epBmsMbReady !== true;
      section._epBmsMbReady = true;

      const targetRole = (itemId) => {
        if (itemId === id) return 'active';
        if (!canNav) return 'idle';
        if (itemId === prevId && prevId !== nextId) return 'prev';
        if (itemId === nextId) return 'next';
        return 'idle';
      };

      const incoming = items.find((item) => targetRole(item.getAttribute('data-ep-bms-mb-item')) === 'active');
      this.updateBmsMbPanelHeight(section, incoming);

      items.forEach((item) => {
        const itemId = item.getAttribute('data-ep-bms-mb-item');
        const nextRole = targetRole(itemId);
        const prevRole = item.dataset.epBmsMbRole || 'idle';

        if (isInit) {
          item.style.transition = 'none';
          this.setBmsMbItemRole(item, nextRole === 'idle' ? 'exit-right' : nextRole);
          void item.offsetWidth;
          item.style.transition = '';
          item.dataset.epBmsMbRole = nextRole;
          return;
        }

        if (prevRole === nextRole) {
          this.setBmsMbItemRole(item, nextRole === 'idle' ? 'exit-right' : nextRole);
          return;
        }

        // 从屏外入场：先无动画停在对应一侧，再过渡到目标态
        if (prevRole === 'idle' && (nextRole === 'next' || nextRole === 'prev')) {
          item.style.transition = 'none';
          this.setBmsMbItemRole(item, nextRole === 'next' ? 'exit-right' : 'exit-left');
          void item.offsetWidth;
          item.style.transition = '';
        }

        const visualRole = nextRole === 'idle'
          ? (prevRole === 'next' ? 'exit-right' : 'exit-left')
          : nextRole;

        this.setBmsMbItemRole(item, visualRole);
        item.dataset.epBmsMbRole = nextRole;
      });
    }

    updateBmsMbPanelHeight(section, item) {
      const panel = section.querySelector('[data-ep-bms-mb-panel]');
      if (!panel) return;

      if (window.innerWidth > 1023.98) {
        panel.style.removeProperty('--ep-bms-mb-h');
        return;
      }

      const active = item || panel.querySelector('.ep-bms__mb-item.is-active');
      const body = active && active.querySelector('.ep-bms__mb-item-body');
      const panelW = panel.getBoundingClientRect().width;
      const minH = Math.round(panelW * (40 / 375));
      if (!body || !panelW) {
        panel.style.setProperty('--ep-bms-mb-h', `${Math.max(minH, Math.round(panelW * (134 / 375)))}px`);
        return;
      }

      // 按展开后的宽度测量，避免在 40px 窄条上把文案量成一长列
      const expandedW = panelW * (1 - 0.256);
      const probe = body.cloneNode(true);
      probe.style.cssText = [
        'position:absolute',
        'left:0',
        'top:0',
        'visibility:hidden',
        'pointer-events:none',
        'opacity:1',
        `width:${Math.max(0, expandedW)}px`,
        'height:auto',
        'max-height:none'
      ].join(';');
      panel.appendChild(probe);
      const contentH = Math.ceil(probe.scrollHeight);
      probe.remove();
      panel.style.setProperty('--ep-bms-mb-h', `${Math.max(minH, contentH)}px`);
    }

    updateBmsUiTrack(section) {
      const track = section.querySelector('[data-ep-bms-ui-track]');
      if (!track) return;
      const items = Array.from(track.querySelectorAll('[data-ep-bms-ui]'));
      if (!items.length) {
        track.hidden = true;
        return;
      }
      track.hidden = false;

      const active = items.find((item) => item.classList.contains('is-active')) || items[0];
      const visual = section.querySelector('.ep-bms__visual');
      if (!visual || !active) return;

      const visualW = visual.clientWidth;
      const visualH = visual.clientHeight;
      if (!visualW || !visualH) return;

      const isMb = window.innerWidth <= 1023.98;
      const trackStyles = window.getComputedStyle(track);
      const gap = Number.parseFloat(trackStyles.gap)
        || Number.parseFloat(trackStyles.columnGap)
        || 0;
      const index = Math.max(0, items.indexOf(active));

      // 用未受 transform/scale 影响的布局尺寸计算，避免错位累积
      let activeCenterLocalX = 0;
      for (let i = 0; i < index; i += 1) {
        activeCenterLocalX += items[i].offsetWidth + gap;
      }
      activeCenterLocalX += active.offsetWidth / 2;

      // PC：窗口 left 536 / 宽 736 / top 220，当前屏对准产品屏幕中心 x=897
      // MB：背景 375×513，屏幕 top 由 CSS，此处只做水平居中
      if (isMb) {
        const nextX = visualW * 0.5 - activeCenterLocalX;
        track.style.transform = `translate3d(${nextX}px, 0px, 0px)`;
        return;
      }

      const windowLeft = visualW * (536 / 1280);
      const targetCenterX = visualW * (897 / 1280) - windowLeft;
      const nextX = targetCenterX - activeCenterLocalX;
      track.style.transform = `translate3d(${nextX}px, 0px, 0px)`;
    }

    clearBmsLastTipWait(section) {
      if (section._epBmsTipTimer) {
        window.clearTimeout(section._epBmsTipTimer);
        section._epBmsTipTimer = null;
      }
      const track = section._epBmsTipTrack;
      const onEnd = section._epBmsTipOnEnd;
      if (track && onEnd) {
        track.removeEventListener('transitionend', onEnd);
      }
      section._epBmsTipTrack = null;
      section._epBmsTipOnEnd = null;
    }

    syncBmsLastTip(section, { settled = false } = {}) {
      const tip = section.querySelector('[data-ep-bms-tip]');
      if (!tip) return;

      const ids = section._epBmsIds || [];
      const lastId = ids[ids.length - 1];
      const isLast = String(section._epBmsSlideId) === String(lastId);

      this.clearBmsLastTipWait(section);

      if (!isLast) {
        tip.classList.remove('is-active');
        return;
      }

      if (settled) {
        tip.classList.add('is-active');
        return;
      }

      tip.classList.remove('is-active');

      const show = () => {
        this.clearBmsLastTipWait(section);
        if (String(section._epBmsSlideId) !== String(lastId)) return;
        tip.classList.add('is-active');
      };

      const track = section.querySelector('[data-ep-bms-ui-track]');
      if (!track) {
        show();
        return;
      }

      const onEnd = (event) => {
        if (event && event.target !== track) return;
        if (event && event.propertyName && event.propertyName !== 'transform') return;
        show();
      };
      section._epBmsTipTrack = track;
      section._epBmsTipOnEnd = onEnd;
      track.addEventListener('transitionend', onEnd);
      section._epBmsTipTimer = window.setTimeout(show, 520);
    }

    bindBmsSwipe(section, goTo) {
      const visual = section.querySelector('.ep-bms__visual');
      if (!visual) return;
      if ((section._epBmsIds || []).length < 2) return;

      let startX = 0;
      let startY = 0;
      let tracking = false;
      let axis = null;

      const stopListen = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
      };

      const onMove = (event) => {
        if (!tracking) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (axis != null || (Math.abs(dx) <= 8 && Math.abs(dy) <= 8)) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') {
          tracking = false;
          axis = null;
          stopListen();
          this.startBmsAutoplay(section);
        }
      };

      const onEnd = (event) => {
        stopListen();
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        const wentHorizontal = axis === 'x' && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy);
        axis = null;
        if (wentHorizontal) {
          goTo(section._epBmsIndex + (dx < 0 ? 1 : -1), { user: true });
          return;
        }
        this.startBmsAutoplay(section);
      };

      visual.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, a, .ep-bms__copy, .ep-bms__mb-bar, .ep-bms__nav')) return;
        tracking = true;
        axis = null;
        startX = event.clientX;
        startY = event.clientY;
        this.pauseBmsAutoplay(section);
        window.addEventListener('pointermove', onMove, { passive: true });
        window.addEventListener('pointerup', onEnd, { passive: true });
        window.addEventListener('pointercancel', onEnd, { passive: true });
      });
    }

    startBmsAutoplay(section) {
      this.pauseBmsAutoplay(section);
      if (section.dataset.autoplay !== '1') return;
      const ids = section._epBmsIds || [];
      if (ids.length < 2) return;
      const interval = Number(section.dataset.interval) || 5000;
      this.startBmsProgress(section);
      section._epBmsTimer = window.setInterval(() => {
        const next = ((section._epBmsIndex || 0) + 1) % ids.length;
        section._epBmsIndex = next;
        this.setBmsSlide(section, ids[next]);
        this.updateBmsUiTrack(section);
        this.startBmsProgress(section);
      }, interval);
    }

    pauseBmsAutoplay(section) {
      if (section._epBmsTimer) {
        window.clearInterval(section._epBmsTimer);
        section._epBmsTimer = null;
      }
      this.pauseBmsProgress(section);
    }

    restartBmsAutoplay(section) {
      this.pauseBmsAutoplay(section);
      this.startBmsAutoplay(section);
    }

    initUsageScenes() {
      const sections = this.querySelectorAll('[data-ep-usage]');
      sections.forEach((section) => this.initUsageSection(section));
    }

    initUsageSection(section) {
      if (section.dataset.epUsageBound === '1') return;
      section.dataset.epUsageBound = '1';

      const ids = Array.from(section.querySelectorAll('[data-ep-usage-slide]'))
        .map((el) => el.getAttribute('data-ep-usage-slide'))
        .filter(Boolean);
      if (!ids.length) return;

      section._epUsageIds = ids;
      const activeSlide = section.querySelector('.ep-usage__slide.is-active');
      const activeId = activeSlide ? activeSlide.getAttribute('data-ep-usage-slide') : ids[0];
      section._epUsageIndex = Math.max(0, ids.indexOf(activeId || ids[0]));

      const goTo = (index, { user } = {}) => {
        const next = ((index % ids.length) + ids.length) % ids.length;
        section._epUsageIndex = next;
        this.setUsageSlide(section, ids[next]);
        if (user) this.restartUsageAutoplay(section);
      };

      this.bindUsageSwipe(section, goTo);

      section.querySelectorAll('[data-ep-usage-dot]').forEach((dot) => {
        dot.addEventListener('click', () => {
          goTo(ids.indexOf(dot.getAttribute('data-ep-usage-dot')), { user: true });
        });
      });

      section.querySelectorAll('[data-ep-usage-prev]').forEach((btn) => {
        btn.addEventListener('click', () => goTo(section._epUsageIndex - 1, { user: true }));
      });

      section.querySelectorAll('[data-ep-usage-next]').forEach((btn) => {
        btn.addEventListener('click', () => goTo(section._epUsageIndex + 1, { user: true }));
      });

      section.addEventListener('mouseenter', () => this.pauseUsageAutoplay(section));
      section.addEventListener('mouseleave', () => this.startUsageAutoplay(section));
      section.addEventListener('touchstart', () => this.pauseUsageAutoplay(section), { passive: true });
      section.addEventListener('touchend', () => this.startUsageAutoplay(section), { passive: true });

      this.setUsageSlide(section, ids[section._epUsageIndex]);
      this.startUsageAutoplay(section);
    }

    bindUsageSwipe(section, goTo) {
      const viewport = section.querySelector('.ep-usage__viewport');
      if (!viewport) return;
      if ((section._epUsageIds || []).length < 2) return;

      let startX = 0;
      let startY = 0;
      let tracking = false;
      let axis = null;

      const stopListen = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onEnd);
        window.removeEventListener('pointercancel', onEnd);
      };

      const onMove = (event) => {
        if (!tracking) return;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        if (axis != null || (Math.abs(dx) <= 8 && Math.abs(dy) <= 8)) return;
        axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
        if (axis === 'y') {
          tracking = false;
          axis = null;
          stopListen();
          this.startUsageAutoplay(section);
        }
      };

      const onEnd = (event) => {
        stopListen();
        if (!tracking) return;
        tracking = false;
        const dx = event.clientX - startX;
        const dy = event.clientY - startY;
        const wentHorizontal = axis === 'x' && Math.abs(dx) >= 40 && Math.abs(dx) > Math.abs(dy);
        axis = null;
        if (wentHorizontal) {
          goTo(section._epUsageIndex + (dx < 0 ? 1 : -1), { user: true });
          return;
        }
        this.startUsageAutoplay(section);
      };

      viewport.addEventListener('pointerdown', (event) => {
        if (event.pointerType === 'mouse' && event.button !== 0) return;
        if (event.target.closest('button, a')) return;
        tracking = true;
        axis = null;
        startX = event.clientX;
        startY = event.clientY;
        this.pauseUsageAutoplay(section);
        window.addEventListener('pointermove', onMove, { passive: true });
        window.addEventListener('pointerup', onEnd, { passive: true });
        window.addEventListener('pointercancel', onEnd, { passive: true });
      });
    }

    startUsageAutoplay(section) {
      this.pauseUsageAutoplay(section);
      if (section.dataset.autoplay !== '1') return;
      const ids = section._epUsageIds || [];
      if (ids.length < 2) return;
      const interval = Number(section.dataset.interval) || 5000;
      section._epUsageTimer = window.setInterval(() => {
        const next = ((section._epUsageIndex || 0) + 1) % ids.length;
        section._epUsageIndex = next;
        this.setUsageSlide(section, ids[next]);
      }, interval);
    }

    pauseUsageAutoplay(section) {
      if (section._epUsageTimer) {
        window.clearInterval(section._epUsageTimer);
        section._epUsageTimer = null;
      }
    }

    restartUsageAutoplay(section) {
      this.pauseUsageAutoplay(section);
      this.startUsageAutoplay(section);
    }

    setUsageSlide(section, id) {
      const ids = section._epUsageIds || [];
      const canNav = ids.length > 1;

      section.querySelectorAll('[data-ep-usage-slide]').forEach((slide) => {
        const active = slide.getAttribute('data-ep-usage-slide') === id;
        slide.classList.toggle('is-active', active);
        slide.hidden = !active;
      });

      section.querySelectorAll('[data-ep-usage-dot]').forEach((dot) => {
        dot.classList.toggle('is-active', dot.getAttribute('data-ep-usage-dot') === id);
      });

      const prevBtn = section.querySelector('[data-ep-usage-prev]');
      const nextBtn = section.querySelector('[data-ep-usage-next]');
      if (prevBtn) prevBtn.classList.toggle('is-disabled', !canNav);
      if (nextBtn) nextBtn.classList.toggle('is-disabled', !canNav);
    }

    initFaqs() {
      const sections = this.querySelectorAll('[data-ep-faq]');
      sections.forEach((section) => this.initFaqSection(section));
    }

    initFaqSection(section) {
      if (section.dataset.epFaqBound === '1') return;
      section.dataset.epFaqBound = '1';

      // Prefer class-driven height animation; drop [hidden] which blocks transitions
      section.querySelectorAll('[data-ep-faq-panel]').forEach((panel) => {
        panel.hidden = false;
      });

      section.querySelectorAll('[data-ep-faq-trigger]').forEach((trigger) => {
        trigger.addEventListener('click', () => {
          const item = trigger.closest('[data-ep-faq-item]');
          if (!item) return;
          const willOpen = !item.classList.contains('is-open');

          section.querySelectorAll('[data-ep-faq-item]').forEach((el) => {
            const isTarget = el === item && willOpen;
            el.classList.toggle('is-open', isTarget);
            const btn = el.querySelector('[data-ep-faq-trigger]');
            const panel = el.querySelector('[data-ep-faq-panel]');
            if (btn) btn.setAttribute('aria-expanded', isTarget ? 'true' : 'false');
            if (panel) {
              panel.hidden = false;
              panel.setAttribute('aria-hidden', isTarget ? 'false' : 'true');
            }
          });
        });
      });
    }

    initStoryScrolls() {
      const sections = this.querySelectorAll('[data-ep-story]');
      sections.forEach((section) => this.initStoryScroll(section));
    }

    initStoryScroll(section) {
      if (section.dataset.epStoryBound === '1') return;
      section.dataset.epStoryBound = '1';
      section._epStoryPage = 0;
      section._epStoryVisible = false;

      const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (reduced) {
        this.setStoryPage(section, 1, false);
        return;
      }

      const sticky = section.querySelector('.ep-story__sticky');
      const update = () => {
        const scrollable = section.offsetHeight - window.innerHeight;
        let page = 1;
        if (scrollable > 0) {
          const top = section.getBoundingClientRect().top;
          const progress = Math.min(1, Math.max(0, -top / scrollable));
          page = progress < 0.48 ? 1 : 2;
        }

        if (!section._epStoryVisible) {
          if (page !== section._epStoryPage) this.setStoryPage(section, page, false);
          return;
        }

        if (page !== section._epStoryPage) {
          this.setStoryPage(section, page, true);
        }
      };

      if (sticky && 'IntersectionObserver' in window) {
        const io = new IntersectionObserver((entries) => {
          entries.forEach((entry) => {
            const wasVisible = section._epStoryVisible;
            section._epStoryVisible = entry.isIntersecting && entry.intersectionRatio > 0.2;
            if (section._epStoryVisible && !wasVisible) {
              const page = section._epStoryPage || 1;
              this.setStoryPage(section, page, true);
            }
          });
        }, { threshold: [0.2, 0.4, 0.6] });
        io.observe(sticky);
        section._epStoryIo = io;
        this._epCleanups.push(() => {
          io.disconnect();
          if (section._epStoryIo === io) section._epStoryIo = null;
        });
      } else {
        section._epStoryVisible = true;
      }

      section._epStoryOnScroll = update;
      this.listen(window, 'scroll', update, { passive: true });
      this.listen(window, 'resize', update, { passive: true });
      update();
    }

    setStoryPage(section, page, animate) {
      section._epStoryPage = page;

      section.querySelectorAll('[data-ep-story-bg]').forEach((bg) => {
        bg.classList.toggle('is-active', bg.getAttribute('data-ep-story-bg') === String(page));
      });

      section.querySelectorAll('[data-ep-story-page]').forEach((el) => {
        const isTarget = el.getAttribute('data-ep-story-page') === String(page);
        el.hidden = !isTarget;
        el.classList.toggle('is-active', isTarget);
        el.classList.remove('is-animating', 'is-shown');

        if (!isTarget) return;

        if (animate) {
          void el.offsetWidth;
          el.classList.add('is-animating');
        } else {
          el.classList.add('is-shown');
        }
      });
    }
  }

  if (!customElements.get('electronic-page')) {
    customElements.define('electronic-page', ElectronicPage);
  }
})();
